import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Form from "../../PerfilDispositivo/form";
import Select from "../../../../components/Select";
import helper from "../../../../utils/helper";
import ReactLoading from "react-loading";
import axios from "axios";
import auth from "../../../../utils/auth";
import cfg from "../../../../utils/config";
import lnsDeviceProfile from "../../../../services/lnsImportDeviceProfile";
import { LnsCadastroRapidoContext } from "../../../../contexts/LnsCadastroRapido";

const PerfilDispositivo = forwardRef((props, ref) => {
    const userInfo = auth.getUserInfo();
    const formRef = useRef();
    const { setDeviceProfileId, deviceProfileId, selectedDeviceProfileType, setSelectedDeviceProfileType, setSelectedImportVendor, setSelectedImportDevice, setSelectedImportProfile, selectedImportVendor, applicationId } = useContext(LnsCadastroRapidoContext);

    const [deviceProfileType, setDeviceProfileType] = useState(null);
    const [selectedDeviceProfile, setSelectedDeviceProfile] = useState(null);
    const [deviceProfiles, setDeviceProfiles] = useState([]);
    const [loadingProfiles, setLoadingProfiles] = useState(false);
    const [aplicacaoClienteId, setAplicacaoClienteId] = useState(undefined);
    const [vendor, setVendor] = useState(null)
    const [device, setDevice] = useState(null)
    const [profile, setProfile] = useState(null)
    const [vendorsOptions, setVendorsOptions] = useState([])
    const [devicesOptions, setDevicesOptions] = useState([])
    const [profilesOptions, setProfilesOptions] = useState([])
    const [templateProfile, setTemplateProfile] = useState(null)
    const [isLoading, setIsLoading] = useState(null)

    const deviceProfileOptions = deviceProfiles
        .filter((item) => aplicacaoClienteId == null || item.cliente == aplicacaoClienteId)
        .map((item) => ({ label: item.label, value: item.value }))

    const hasDeviceProfiles = deviceProfileOptions.length;
    const isResolvingProfiles = loadingProfiles || aplicacaoClienteId === undefined;

    const deviceProfileTypeOptions = useMemo(() => {
        const options = [];

        if (hasDeviceProfiles) {
            options.push({
                label: "Usar Perfil Existente",
                value: "selectDeviceProfile"
            },);
        }

        options.push(
            {
                label: !selectedImportVendor ? "Novo Perfil de Dispositivo" : "Perfil de Dispositivo (Importado)",
                value: "newDeviceProfile"
            },
            {
                label: "Importar Template",
                value: "importDeviceProfile"
            }
        );

        return options;
    }, [selectedImportVendor, hasDeviceProfiles]);


    useEffect(() => {
        getDeviceProfiles();
        getVendors()
        getAplicacaoCliente()
    }, []);

    useEffect(() => {
        if (isResolvingProfiles) return;

        if (deviceProfileId && selectedDeviceProfileType) {
            const perfilAtual = deviceProfiles.find((item) => item.value === deviceProfileId);
            const perfilAindaCompativel = !perfilAtual || aplicacaoClienteId == null || perfilAtual.cliente == aplicacaoClienteId;

            if (!perfilAindaCompativel) {
                // usuário voltou e trocou a aplicação por uma de outro cliente; o perfil
                // criado/selecionado antes não serve mais — limpa pra forçar escolher de novo
                setDeviceProfileId(null);
                setSelectedDeviceProfileType(null);
                setSelectedDeviceProfile(null);
                setDeviceProfileType(null);

                helper.dispatchEvent("showAviso", {
                    message: "A aplicação selecionada mudou de cliente — escolha ou crie o perfil de dispositivo novamente."
                });

                return;
            }

            setDeviceProfileType(selectedDeviceProfileType);

            if (selectedDeviceProfileType === "selectDeviceProfile") {
                setSelectedDeviceProfile(deviceProfileId);
            }

            return;
        }

        if (!hasDeviceProfiles) {
            setDeviceProfileType("newDeviceProfile");
            setSelectedDeviceProfileType("newDeviceProfile");

            return;
        }

        setDeviceProfileType(null);
    }, [isResolvingProfiles, hasDeviceProfiles, deviceProfileId, selectedDeviceProfileType, deviceProfiles, aplicacaoClienteId]);

    useImperativeHandle(ref, () => ({
        onNext: async () => {
            if (deviceProfileType === "importDeviceProfile") {
                if (!vendor || !device || !profile) {
                    helper.dispatchEvent("showAviso", { message: "Selecione fornecedor, dispositivo e perfil." });

                    return false;
                }

                const result = await importProfile();

                if (!result?.success) {
                    return false;
                }

                setDeviceProfileId(result.deviceProfileId);
                setSelectedDeviceProfileType("newDeviceProfile");
                setSelectedImportVendor(vendor)

                return true;
            }

            if (deviceProfileType === "newDeviceProfile") {
                const result = await formRef.current?.handleQuickRegister();

                if (!result?.success) {
                    return false;
                }

                setDeviceProfileId(result.deviceProfileId);
                setSelectedDeviceProfileType("newDeviceProfile");

                return true;
            }

            if (deviceProfileType === "selectDeviceProfile") {
                if (!selectedDeviceProfile) {
                    const span = document.getElementById("validacao-device-profile");
                    if (span) span.innerHTML = "Selecione um perfil de dispositivo.";

                    return false;
                }

                setDeviceProfileId(selectedDeviceProfile);
                setSelectedDeviceProfileType("selectDeviceProfile");

                const result = await new Promise((resolve) => {
                    helper.dispatchEvent("showAviso", {
                        message: "Perfil de dispositivo selecionado com sucesso.",
                        callback: () => resolve(true),
                        callbackClose: () => resolve(true)
                    });
                });

                return result;
            }

            const span = document.getElementById("validacao-tipo-device-profile");
            if (span) span.innerHTML = "Selecione o tipo de perfil de dispositivo.";

            return false;
        }
    }));

    const getDeviceProfiles = async () => {
        try {
            setLoadingProfiles(true);

            const data = await lnsDeviceProfile.getDeviceProfiles();
            const items = data.results.map((item) => ({
                label: item.name,
                value: item.id,
                cliente: item.cliente ?? null
            }));

            setDeviceProfiles(items);
        } catch (error) {
            console.error("Erro ao buscar perfis de dispositivo:", error);

            setDeviceProfiles([]);
        } finally {
            setLoadingProfiles(false);
        }
    };

    const getAplicacaoCliente = async () => {
        if (!applicationId) {
            setAplicacaoClienteId(null);
            return;
        }

        try {
            const requestURL = `${cfg.base_api_url}${cfg.api_v2}/${userInfo.empresa}/lns/application/?id=${applicationId}`;
            const configMe = {
                headers: {
                    Authorization: `Bearer ${auth.getToken()}`
                }
            };

            const { data } = await axios.get(requestURL, configMe);
            const aplicacao = data.results?.[0];

            setAplicacaoClienteId(aplicacao ? (aplicacao.cliente ?? null) : null);
        } catch (error) {
            console.error("Erro ao buscar cliente da aplicação:", error);
            setAplicacaoClienteId(null);
        }
    };

    const getVendors = async () => {
        try {
            const data = await lnsDeviceProfile.getVendors();
            const options = data?.results?.map(item => ({
                label: item.name,
                value: item.id
            }));

            setVendorsOptions(options);
        } catch (error) {
            console.error(error);
        }
    };

    const getDevices = async (vendorId) => {
        try {
            const data = await lnsDeviceProfile.getDevices(vendorId);
            const options = data?.results?.map(item => ({
                label: item.name,
                value: item.id
            }));

            setDevicesOptions(options);
        } catch (error) {
            console.error(error);
        }
    };

    const getProfiles = async (vendorId, deviceId) => {
        try {
            const data = await lnsDeviceProfile.getProfiles(vendorId, deviceId);
            const options = data?.results?.map(item => ({
                label: item.name,
                value: item.id
            }));

            setProfilesOptions(options);
        } catch (error) {
            console.error(error);
        }
    };

    const getProfileDetail = async (vendorId, deviceId, profileId) => {
        try {
            const data = await lnsDeviceProfile.getProfileDetail(vendorId, deviceId, profileId);

            setTemplateProfile(data);
        } catch (error) {
            console.error(error);
        }
    };

    const importProfile = async () => {
        try {
            setIsLoading(true);

            const { data, status } = await lnsDeviceProfile.importProfile(vendor, device, profile, aplicacaoClienteId);

            if (status !== 201) {
                return {
                    success: false,
                    deviceProfileId: data.id
                };
            }

            return await new Promise((resolve) => {
                helper.dispatchEvent("showAviso", {
                    message: "Importação realizada com sucesso.",
                    callback: () => {
                        resolve({
                            success: true,
                            deviceProfileId: data.id
                        });
                    },
                    callbackClose: () => {
                        resolve({
                            success: true,
                            deviceProfileId: data.id
                        });
                    }
                });
            });

        } catch (error) {
            helper.dispatchEvent("showAviso", { message: "Erro ao importar perfil." });

            return {
                success: false
            };
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeviceProfileTypeChange = (option) => {
        setDeviceProfileType(option.value);

        const spanTipo = document.getElementById("validacao-tipo-device-profile");

        if (spanTipo) spanTipo.innerHTML = "";

        if (option?.value !== "selectDeviceProfile") {
            setSelectedDeviceProfile(null);
        }
    };

    const handleSelectDeviceProfile = (option) => {
        setSelectedDeviceProfile(option.value);

        const span = document.getElementById("validacao-device-profile");
        if (span) span.innerHTML = "";
    };

    const handleVendorChange = (selected) => {
        const newVendor = selected?.value ? selected?.value : selected

        if (vendor === newVendor) return

        setVendor(newVendor)
        setDevice(null)
        setProfile(null)
        setDevicesOptions([])
        setProfilesOptions([])

        if (newVendor) {
            getDevices(newVendor)
        }
    }

    const handleDeviceChange = (selected) => {
        const newDevice = selected?.value ? selected?.value : selected

        if (device === newDevice) return

        setDevice(newDevice)
        setProfile(null)
        setProfilesOptions([])

        if (newDevice && vendor) {
            getProfiles(vendor, newDevice)
        }
    }

    const handleProfileChange = (selected) => {
        const newProfile = selected?.value ? selected?.value : selected

        if (profile === newProfile) return

        setProfile(newProfile)
        setTemplateProfile(null)

        if (newProfile && vendor && device) {
            getProfileDetail(vendor, device, newProfile)
        }
    }

    return (
        <div className="hdv-form-default">
            <div className="hdv-default-header mx-3">
                <span className="screen-menu-desc">
                    <i
                        className="fa fa-info-circle fa-20px"
                        title='Informações do Cadastro de Perfil de Dispositivos'
                        style={{
                            cursor: "pointer",
                            marginRight: '5px',
                            marginTop: '10px',
                        }}
                        onClick={() =>
                            helper.dispatchEvent("showAviso", {
                                message: `
                                  Define como o dispositivo opera dentro do protocolo LoRa. Crie um perfil para cada linha de dispositivos que você utiliza.<br /><br />
                                  Antes de criar o perfil, identifique o método de autenticação do seu dispositivo:<br /><br />
                                  <strong>OTAA (recomendado)</strong> — O dispositivo negocia as chaves com a rede automaticamente. Você precisará do <strong>DevEUI</strong>, <strong>AppEUI</strong> e <strong>AppKey</strong>.<br /><br />
                                  <strong>ABP</strong> — As chaves são fixas e gravadas diretamente no dispositivo. Você precisará do <strong>DevAddr</strong>, <strong>NwkSKey</strong> e <strong>AppSKey</strong>.<br /><br />
                                  ⚠️ O método escolhido aqui deve ser o mesmo configurado no hardware, pois as chaves exigidas no cadastro mudam conforme o tipo.
                                `,
                            })
                        }
                    />
                    {deviceProfileId ? <h4>Editar Perfil</h4> : <h4>Novo Perfil</h4>}
                    <div className="gray-background">
                        <i className="las la-project-diagram la-2x"></i>
                    </div>
                </span>
            </div>

            {isResolvingProfiles ? (
                <div className="loading-screen-overlay">
                    <ReactLoading type="spin" color="var(--background-menu)" width={40} />
                </div>
            ) : (
                <>
                    <div className="row">
                        <div className="col-md-6">
                            <label className="required">Tipo de Perfil de Dispositivo</label>
                            <Select
                                options={deviceProfileTypeOptions}
                                value={deviceProfileType}
                                onChange={handleDeviceProfileTypeChange}
                                resolveValueFromOptions
                                disabled={deviceProfileId}
                            />
                            <span id="validacao-tipo-device-profile"></span>
                        </div>

                        {deviceProfileType === "selectDeviceProfile" && (
                            <div className="col-md-6">
                                <label className="required">Perfil de Dispositivo</label>
                                <Select
                                    options={deviceProfileOptions}
                                    value={selectedDeviceProfile}
                                    onChange={handleSelectDeviceProfile}
                                    resolveValueFromOptions
                                    disabled={isResolvingProfiles || deviceProfileId}
                                />
                                <span id="validacao-device-profile"></span>
                            </div>
                        )}

                        {deviceProfileType === "importDeviceProfile" && (
                            <div className={"col-md-6"}>
                                <label className="required">Fornecedor</label>
                                <Select
                                    value={vendor}
                                    onChange={handleVendorChange}
                                    options={vendorsOptions}
                                    resolveValueFromOptions
                                />
                            </div>
                        )}
                    </div>

                    {deviceProfileType === "importDeviceProfile" && (
                        <div className="row">
                            <div className={"col-md-6"}>
                                <label className="required">Dispositivo</label>
                                <Select
                                    value={device}
                                    onChange={handleDeviceChange}
                                    options={devicesOptions}
                                    resolveValueFromOptions
                                    disabled={!vendor}
                                />
                            </div>

                            <div className={"col-md-6"}>
                                <label className="required">Perfil</label>
                                <Select
                                    value={profile}
                                    onChange={handleProfileChange}
                                    options={profilesOptions}
                                    resolveValueFromOptions
                                    disabled={!device}
                                />
                            </div>
                        </div>
                    )}

                    {(deviceProfileType === "newDeviceProfile" || (deviceProfileType === "importDeviceProfile" && profile)) && (
                        <>
                            <hr />
                            <Form
                                ref={formRef}
                                isQuickRegister
                                templateProfile={templateProfile}
                                deviceProfileId={deviceProfileId}
                                isTemplate={deviceProfileType === "importDeviceProfile"}
                                lockedClienteId={aplicacaoClienteId}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
});

export default PerfilDispositivo;
